"use client"

import { MAX_QUALITY_READS } from "@/lib/conversation-quality/page-reads"
import {
  getQualityReadsAction,
  type QualityReadKind,
  type QualityReads,
} from "@/server-actions/conversation-quality-reads"

type ReadInput<K extends QualityReadKind> = Parameters<QualityReads[K]>[0]
type ReadResult<K extends QualityReadKind> = Awaited<ReturnType<QualityReads[K]>>

interface PendingRead {
  kind: QualityReadKind;
  input: unknown;
  resolve: (result: unknown) => void;
  reject: (error: unknown) => void;
}

let queue: PendingRead[] = []
let scheduled = false

/**
 * One Conversation Quality read, sent with every other read started in the same tick as a single server action:
 * Next.js dispatches a client's server actions one at a time, so separate calls would queue behind each other.
 */
export function readQuality<K extends QualityReadKind>(kind: K, input: ReadInput<K>): Promise<ReadResult<K>> {
  return new Promise<ReadResult<K>>((resolve, reject) => {
    queue.push({ kind, input, resolve: resolve as (result: unknown) => void, reject })
    if (scheduled) return
    scheduled = true
    // A macrotask, so every query a render commits subscribes before the batch leaves.
    setTimeout(flush, 0)
  })
}

function flush() {
  const pending = queue
  queue = []
  scheduled = false

  for (let start = 0; start < pending.length; start += MAX_QUALITY_READS) {
    void send(pending.slice(start, start + MAX_QUALITY_READS))
  }
}

async function send(batch: PendingRead[]) {
  try {
    const results = await getQualityReadsAction({ reads: batch.map(({ kind, input }) => ({ kind, input })) })
    // A refused batch answers once for every read in it, which the hooks raise as their own error.
    batch.forEach((read, index) => read.resolve(Array.isArray(results) ? results[index] : results))
  } catch (error) {
    for (const read of batch) read.reject(error)
  }
}
